import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wallet, ShieldCheck, ShieldAlert, Mail, HeartHandshake } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, fmt, StatusBadge, fmtDateTime, EmptyState } from "@/components/shared";

export default function Withdrawal() {
  const { user, refresh } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [network, setNetwork] = useState("TRC20");
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(user?.available_balance || 0);
  const [code, setCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [requires2fa, setRequires2fa] = useState(false);
  const [minWithdrawal, setMinWithdrawal] = useState(0);
  const [charityRate, setCharityRate] = useState(0.10);
  const method = user?.two_factor_method;

  const load = async () => {
    const [w, me, s] = await Promise.all([api.get("/withdrawals"), api.get("/auth/me"), api.get("/settings/public")]);
    setWithdrawals(w.data); setBalance(me.data.available_balance); setRequires2fa(!!s.data.withdrawal_requires_2fa);
    setMinWithdrawal(Number(s.data.minimum_withdrawal) || 0); setCharityRate(Number(s.data.charity_rate) || 0.10);
  };
  useEffect(() => { load(); }, []);

  const sendCode = async () => {
    setSendingCode(true);
    try { await api.post("/withdrawals/request-code"); toast.success("Verification code sent to your email"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSendingCode(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than 0");
    if (minWithdrawal && amt < minWithdrawal) return toast.error(`Minimum withdrawal is ${fmt(minWithdrawal)} USDT`);
    if (amt > balance) return toast.error("Insufficient available balance");
    if (!address.trim()) return toast.error("Wallet address is required");
    if (method && code.trim().length < 6) return toast.error("Enter your 6-digit verification code");
    setLoading(true);
    try {
      await api.post("/withdrawals", { amount: amt, network, wallet_address: address, code: method ? code.trim() : undefined });
      toast.success("Withdrawal request submitted successfully.");
      setAmount(""); setAddress(""); setCode("");
      load(); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const blocked = requires2fa && !method;
  const gross = Math.max(0, parseFloat(amount) || 0);
  const charity = Math.round(gross * charityRate * 100) / 100;
  const net = Math.round((gross - charity) * 100) / 100;

  return (
    <div className="space-y-6" data-testid="withdrawal-page">
      <div>
        <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Withdrawal</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Request Withdrawal</h1>
        <p className="text-sm text-slate-400 mt-2">Requests are reviewed before processing. Funds are never sent automatically.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4 flex items-center gap-3">
            <Wallet className="w-5 h-5 text-cyan-300" />
            <div><p className="text-xs text-slate-400">Available Balance</p><p className="font-mono text-xl font-bold text-white" data-testid="withdraw-available">{fmt(balance)} USDT</p></div>
          </div>
          <div className="flex gap-3">
            {["TRC20", "BEP20"].map((n) => (
              <button key={n} type="button" data-testid={`withdraw-network-${n}`} onClick={() => setNetwork(n)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${network === n ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>
                {n === "TRC20" ? "TRC20 (Tron)" : "BEP20 (BNB)"}
              </button>
            ))}
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Amount (USDT)</label>
            <input data-testid="withdraw-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" placeholder="0.00" />
            <button type="button" onClick={() => setAmount(String(balance))} className="text-xs text-cyan-400 mt-1 hover:underline">Use max</button>
            {minWithdrawal > 0 && <span className="text-xs text-slate-500 ml-3" data-testid="withdraw-min-note">Minimum {fmt(minWithdrawal)} USDT</span>}
          </div>
          <div className="bg-slate-900/60 border border-cyan-500/15 rounded-xl p-4 space-y-2" data-testid="withdraw-breakdown">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400">Withdrawal Amount</span><span className="font-mono text-white" data-testid="breakdown-gross">{fmt(gross)} USDT</span></div>
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400 inline-flex items-center gap-1.5"><HeartHandshake className="w-4 h-4 text-rose-300" /> Charity Fund ({Math.round(charityRate * 100)}%)</span><span className="font-mono text-rose-300" data-testid="breakdown-charity">− {fmt(charity)} USDT</span></div>
            <div className="border-t border-white/5 pt-2 flex items-center justify-between"><span className="text-sm text-slate-200 font-medium">You Will Receive</span><span className="font-mono text-lg font-bold text-emerald-300" data-testid="breakdown-net">{fmt(net)} USDT</span></div>
            <p className="text-[11px] text-slate-500 pt-1" data-testid="charity-note">10% of every withdrawal goes to our Charity Fund.</p>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Wallet Address</label>
            <input data-testid="withdraw-address" value={address} onChange={(e) => setAddress(e.target.value)}
              className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 font-mono text-sm focus:border-cyan-400 focus:outline-none" placeholder="Your receiving address" />
          </div>
          {method && (
            <div data-testid="withdraw-2fa-block" className="bg-slate-900/40 border border-cyan-500/20 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-200"><ShieldCheck className="w-4 h-4 text-cyan-400" /> Two-factor confirmation</div>
              <p className="text-xs text-slate-500">{method === "totp" ? "Enter the current code from your authenticator app." : "We'll email you a one-time code to confirm this withdrawal."}</p>
              <div className="flex gap-2">
                <input data-testid="withdraw-2fa-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={8} placeholder="123456"
                  className="flex-1 bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 font-mono tracking-widest focus:border-cyan-400 focus:outline-none" />
                {method === "email" && (
                  <button type="button" onClick={sendCode} disabled={sendingCode} data-testid="withdraw-2fa-send" className="glass glass-hover px-4 rounded-lg text-sm text-cyan-300 inline-flex items-center gap-2 disabled:opacity-60">
                    {sendingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send code
                  </button>
                )}
              </div>
            </div>
          )}
          {blocked && (
            <div data-testid="withdraw-2fa-required" className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
              <div>Two-factor authentication is required for withdrawals. <Link to="/profile" className="underline text-amber-100 hover:text-white" data-testid="withdraw-enable-2fa-link">Enable it in your profile</Link> to continue.</div>
            </div>
          )}
          <button type="submit" disabled={loading || blocked} data-testid="withdraw-submit"
            className="btn-glow w-full py-3 rounded-lg font-semibold text-slate-950 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Request
          </button>
        </form>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg text-white mb-1">Withdrawal History</h3>
          <p className="text-xs text-slate-500 mb-5">10% of every withdrawal goes to our Charity Fund.</p>
          {withdrawals.length ? (
            <div className="space-y-3">
              {withdrawals.map((w) => (
                <div key={w.id} className="bg-slate-900/40 rounded-xl p-4 border border-white/5" data-testid={`withdrawal-history-${w.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-white">{fmt(w.gross_amount ?? w.amount)} USDT</p>
                    <StatusBadge status={w.status} />
                  </div>
                  {w.charity_amount != null && (
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div><p className="text-slate-500">Gross</p><p className="font-mono text-slate-200">{fmt(w.gross_amount)}</p></div>
                      <div><p className="text-slate-500">Charity (10%)</p><p className="font-mono text-rose-300">− {fmt(w.charity_amount)}</p></div>
                      <div><p className="text-slate-500">Net Payout</p><p className="font-mono text-emerald-300">{fmt(w.net_amount)}</p></div>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-1">{w.network} · {fmtDateTime(w.created_at)}</p>
                  <p className="font-mono text-xs text-slate-600 break-all mt-1">{w.wallet_address}</p>
                  {w.status === "rejected" && w.rejection_reason && <p className="text-xs text-red-400 mt-1">Reason: {w.rejection_reason}</p>}
                </div>
              ))}
            </div>
          ) : <EmptyState title="No withdrawals yet" subtitle="Your requests will appear here." />}
        </div>
      </div>
    </div>
  );
}
